import React, { useMemo } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { darkColors } from '../constants/colors';
import { spacing } from '../constants/spacing';
import { typography } from '../constants/typography';
import { useAppColors } from '../context/PreferencesContext';
import { formatDisplayLabel } from '../utils/camelCaseLabel';
import { getLeaveStatusCategory } from '../utils/leaveStatus';
import { getWorkflowStatusCategory } from '../utils/workflowStatus';

type Props = {
  label: string;
  statusCode?: string | null;
};

function resolveCategory(label: string, statusCode?: string | null) {
  if (statusCode) {
    const fromWorkflow = getWorkflowStatusCategory(statusCode);
    if (fromWorkflow !== 'neutral') return fromWorkflow;
    const fromLeave = getLeaveStatusCategory(statusCode);
    if (fromLeave !== 'neutral') return fromLeave;
  }

  const normalized = label.trim().toUpperCase().replace(/\s+/g, '_');
  const fromLabelCode = getLeaveStatusCategory(normalized);
  if (fromLabelCode !== 'neutral') return fromLabelCode;
  return getWorkflowStatusCategory(normalized);
}

export function LeaveStatusBadge({ label, statusCode }: Props) {
  const palette = useAppColors();
  const isDark = palette.background === darkColors.background;
  const displayLabel = formatDisplayLabel(label);
  const category = resolveCategory(label, statusCode);

  const toneStyle = useMemo(() => {
    switch (category) {
      case 'success':
        return {
          backgroundColor: isDark ? '#054F31' : '#DCFCE7',
          color: isDark ? '#6CE9A6' : '#067647',
        };
      case 'warning':
        return {
          backgroundColor: isDark ? '#7A2E0E' : '#FEF0C7',
          color: isDark ? '#FEC84B' : '#B54708',
        };
      case 'danger':
        return {
          backgroundColor: isDark ? '#7A271A' : '#FEE4E2',
          color: isDark ? '#FDA29B' : '#B42318',
        };
      case 'info':
        return {
          backgroundColor: isDark ? '#102A56' : '#E0EAFF',
          color: isDark ? '#84ADFF' : '#175CD3',
        };
      default:
        return {
          backgroundColor: palette.border,
          color: palette.textPrimary,
        };
    }
  }, [category, isDark, palette.border, palette.textPrimary]);

  return (
    <View
      accessibilityRole="text"
      accessibilityLabel={`Status ${displayLabel}`}
      style={[styles.badge, { backgroundColor: toneStyle.backgroundColor }]}
    >
      <Text style={[styles.text, { color: toneStyle.color }]}>{displayLabel}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  badge: {
    alignSelf: 'flex-start',
    borderRadius: 8,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
  },
  text: {
    ...typography.caption,
    fontWeight: '700',
  },
});
